import type { ScanResult, QrResult, FieldConfidence } from "../../types/scan";
import { preprocessImage, cropToCardRegion, cropToOcrRegion, cropToQrRegion } from "./imagePreprocess";
import {
  runOcrBatch,
  mergeOcrTexts,
  parseOcrFieldsWithConfidence,
} from "./ocr";
import { readQrFromDataUrl } from "./qr";
import { fetchQrUrl, type FetchQrUrlBackendConfig } from "./fetchQrUrl";

export type ScanProgress = "preprocess" | "ocr" | "qr" | "fetch";

/** Optional partial result when a step completes (for progressive overlay). */
export type OnProgressPartial = Partial<
  Pick<ScanResult, "firstname" | "lastname" | "company" | "ocrRawText" | "qrResult" | "fieldConfidence">
>;

export type OnProgress = (step: ScanProgress, partial?: OnProgressPartial) => void;

export interface ProcessBtpImageOptions {
  /** When set (e.g. from live QR scan), QR step is skipped and this URL is used. */
  qrUrl?: string;
  /** Extra frames for multi-frame OCR (cropped to card, then merged by line voting). */
  extraFramesForOcr?: string[];
  /** Backend config to verify QR via API proxy (avoids CORS). */
  backendQrConfig?: FetchQrUrlBackendConfig | null;
}

const LOG_PREFIX = "[Scan]";
const toSecs = (ms: number): string => `${(ms / 1000).toFixed(2)}s`;

/**
 * Process a BTP card image: preprocess, then use only overlay regions.
 * - Saved image (btp_card_image): inside main (white) card overlay only.
 * - OCR: inside OCR (amber) overlay only.
 * - QR: inside QR (green) overlay only; if options.qrUrl from live scan, QR step is skipped.
 */
export async function processBtpImage(
  dataUri: string,
  onProgress?: OnProgress,
  options?: ProcessBtpImageOptions
): Promise<ScanResult> {
  const totalStart = performance.now();
  console.log(`${LOG_PREFIX} Starting pipeline…`);

  onProgress?.("preprocess");
  const preprocessStart = performance.now();
  let t0 = performance.now();
  const preprocessed = await preprocessImage(dataUri);
  console.log(`${LOG_PREFIX} preprocess image took ${toSecs(performance.now() - t0)}`);
  t0 = performance.now();
  const croppedCard = await cropToCardRegion(preprocessed);
  console.log(`${LOG_PREFIX} crop card region took ${toSecs(performance.now() - t0)}`);
  t0 = performance.now();
  const croppedForOcr = await cropToOcrRegion(preprocessed);
  console.log(`${LOG_PREFIX} crop OCR region took ${toSecs(performance.now() - t0)}`);
  console.log(`${LOG_PREFIX} preprocess took ${toSecs(performance.now() - preprocessStart)}`);

  onProgress?.("ocr");
  const ocrStart = performance.now();
  const extraUris = options?.extraFramesForOcr ?? [];
  const ocrInputUris: string[] = [croppedForOcr];
  if (extraUris.length > 0) {
    const extraPrepStart = performance.now();
    const extraCropped = await Promise.all(
      extraUris.map((uri) =>
        preprocessImage(uri).then((prep) => cropToOcrRegion(prep))
      )
    );
    console.log(`${LOG_PREFIX} ${extraUris.length} extra frames prep took ${toSecs(performance.now() - extraPrepStart)}`);
    ocrInputUris.push(...extraCropped);
  }
  console.log(`${LOG_PREFIX} OCR started on ${ocrInputUris.length} frame(s)`);
  const ocrResults = await runOcrBatch(ocrInputUris);
  console.log(`${LOG_PREFIX} OCR total took ${toSecs(performance.now() - ocrStart)}`);
  const rawTexts = ocrResults.map((r) => r.rawText).filter(Boolean);
  const linesPerFrame = ocrResults.every((r) => r.lines != null && r.lines.length > 0)
    ? ocrResults.map((r) => r.lines!)
    : undefined;
  const mergeStart = performance.now();
  const mergedRawText =
    rawTexts.length > 1
      ? mergeOcrTexts(rawTexts, linesPerFrame, { confidenceThreshold: 80 })
      : rawTexts[0] ?? "";
  if (rawTexts.length > 1) {
    console.log(`${LOG_PREFIX} OCR merge took ${toSecs(performance.now() - mergeStart)}`);
  }
  const ocrResult = ocrResults[0];
  const ocrText = mergedRawText || ocrResult.rawText || "";
  const { fields: ocrFields, confidence: ocrFieldConfidence } = parseOcrFieldsWithConfidence(
    linesPerFrame,
    ocrText || undefined
  );

  onProgress?.("ocr", {
    firstname: ocrFields.firstname,
    lastname: ocrFields.lastname,
    company: ocrFields.company,
    ocrRawText: ocrText || undefined,
    fieldConfidence: ocrFieldConfidence,
  });

  let qrUrl: string | null = options?.qrUrl ?? null;
  if (qrUrl == null) {
    onProgress?.("qr");
    const qrStart = performance.now();
    const croppedForQr = await cropToQrRegion(preprocessed);
    qrUrl = await readQrFromDataUrl(croppedForQr);
    console.log(`${LOG_PREFIX} QR read took ${toSecs(performance.now() - qrStart)}`);
  } else {
    console.log(`${LOG_PREFIX} qr: skipped (URL from live scan)`);
  }

  let qrResult: QrResult | undefined;
  if (qrUrl) {
    if (options?.backendQrConfig?.baseUrl && options.backendQrConfig.token) {
      onProgress?.("fetch");
      const fetchStart = performance.now();
      qrResult = await fetchQrUrl(qrUrl, options.backendQrConfig);
      console.log(`${LOG_PREFIX} URL fetch took ${toSecs(performance.now() - fetchStart)} → ${qrResult.status}`);
    } else {
      console.log(`${LOG_PREFIX} skip API check (no backend config or token), qrResult = pending`);
      qrResult = { status: "pending", url: qrUrl, message: "URL lue (non vérifiée)" };
    }
  } else {
    qrResult = { status: "pending", message: "QR non lu" };
  }

  onProgress?.("fetch", { qrResult });

  console.log(`${LOG_PREFIX} pipeline took ${toSecs(performance.now() - totalStart)}`);

  // Prefer card number from QR API (identifiantCarte) over OCR when available
  const btpNumber = qrResult?.btp_number ?? ocrFields.btp_number ?? undefined;
  const ocrTimedOut = ocrResults.some((r) => r.timedOut);

  const fieldConfidence: FieldConfidence = { ...ocrFieldConfidence };
  if (qrResult?.btp_number) {
    fieldConfidence.btp_number = 100;
  }

  return {
    btp_card_image: croppedCard,
    btp_number: btpNumber,
    firstname: ocrFields.firstname ?? undefined,
    lastname: ocrFields.lastname ?? undefined,
    company: ocrFields.company ?? undefined,
    qrResult,
    ocrRawText: ocrText || undefined,
    ocrTimedOut: ocrTimedOut || undefined,
    fieldConfidence: Object.keys(fieldConfidence).length > 0 ? fieldConfidence : undefined,
  };
}

/**
 * Build a short comment line from scan result for the control comment.
 */
export function scanResultToCommentLine(result: ScanResult): string {
  const q = result.qrResult;
  if (!q) return "";
  if (!q.url) return "QR code non détecté.";
  if (q.status === "valid") return "Carte validée.";
  if (q.status === "invalid") return q.message ? `Carte invalide: ${q.message}` : "Carte invalide.";
  return q.message || "Vérification à la synchronisation.";
}
